import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  storeOfflineData,
  getOfflineData,
  queueSyncRequest,
  getPendingSyncRequests,
  removeSyncRequest,
} from "../utils/offlineStorage";

interface OfflineContextType {
  isOnline: boolean;
  syncPending: boolean;
  syncData: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export const OfflineProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncPending, setSyncPending] = useState(false);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      await syncData();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check for pending sync on mount
    checkPendingSync();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const checkPendingSync = async () => {
    const pending = await getPendingSyncRequests();
    setSyncPending(pending.length > 0);
  };

  const syncData = async () => {
    try {
      const pendingRequests = await getPendingSyncRequests();

      if (pendingRequests.length === 0) {
        setSyncPending(false);
        return;
      }

      setSyncPending(true);

      for (const request of pendingRequests) {
        try {
          const response = await fetch(request.url, {
            method: request.method,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(request.body),
          });

          if (response.ok) {
            await removeSyncRequest(request.id!);
          }
        } catch (error) {
          console.error("Error syncing request:", error);
        }
      }

      setSyncPending(false);
    } catch (error) {
      console.error("Error during sync:", error);
      setSyncPending(false);
    }
  };

  return (
    <OfflineContext.Provider value={{ isOnline, syncPending, syncData }}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error("useOffline must be used within an OfflineProvider");
  }
  return context;
};
